import React, { useState, useEffect } from 'react';
import AppShell from '../components/AppShell';
import Toast from '../components/Toast';
import api, { formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';
import { COUNTRIES, COUNTRY_BY_CODE, countryFlag } from '../lib/countries';

/**
 * Account Settings — auth/account-only rows.
 * Kept in this page (per user feedback, 2026-07-15):
 *   - Email · Password · Two-factor authentication
 *
 * Sign-out lives exclusively in the avatar menu (Phase 9.6 cleanup —
 * removed from Account Settings since it duplicated the avatar menu action).
 *
 * Moved OUT into dedicated routes:
 *   Profile → /profile · Refer friends → /referrals · Preferences → /preferences ·
 *   Manage/Start my Clan → /manage-clan or /create-clan.
 */
export default function AccountSettings() {
  const { user, refreshMe } = useAuth();

  const [toast, setToast] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwOk, setPwOk] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  // Change-email modal state
  const [emOpen, setEmOpen] = useState(false);
  const [emStep, setEmStep] = useState(1);  // 1 = request, 2 = OTP entry
  const [emNewEmail, setEmNewEmail] = useState('');
  const [emPassword, setEmPassword] = useState('');
  const [emOtp, setEmOtp] = useState('');
  const [emErr, setEmErr] = useState('');
  const [emBusy, setEmBusy] = useState(false);

  // Invited-by chip — { handle, display_name, avatar } or null
  const [invitedBy, setInvitedBy] = useState(null);
  useEffect(() => {
    api.get('/profile/invited-by')
      .then(({ data }) => setInvitedBy(data.referrer))
      .catch(() => {});
  }, []);

  const closeEmailModal = () => {
    setEmOpen(false);
    setEmStep(1);
    setEmNewEmail(''); setEmPassword(''); setEmOtp('');
    setEmErr('');
  };

  const requestOtp = async () => {
    if (emBusy) return;
    setEmBusy(true); setEmErr('');
    try {
      await api.post('/auth/change-email/request', {
        new_email: emNewEmail.trim(),
        password: emPassword,
      });
      setEmStep(2);
      setEmPassword('');
      setToast({ msg: `📬 Code sent to ${emNewEmail.trim()}` });
    } catch (ex) {
      setEmErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not send code.');
    } finally {
      setEmBusy(false);
    }
  };

  const confirmOtp = async () => {
    if (emBusy) return;
    setEmBusy(true); setEmErr('');
    try {
      await api.post('/auth/change-email/confirm', { otp: emOtp.trim() });
      await refreshMe();
      setToast({ msg: '✓ Email updated' });
      closeEmailModal();
    } catch (ex) {
      setEmErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not confirm code.');
    } finally {
      setEmBusy(false);
    }
  };

  // Phase 9.7 — DM gating toggle
  const [dmGating, setDmGating] = useState(null); // 'open' | 'bit_gated' | null=loading
  useEffect(() => {
    api.get('/dm/settings/gating').then(({ data }) => setDmGating(data.dm_gating || 'open')).catch(() => setDmGating('open'));
  }, []);
  const flipGating = async () => {
    const next = dmGating === 'open' ? 'bit_gated' : 'open';
    setDmGating(next);
    try {
      await api.patch('/dm/settings/gating', { dm_gating: next });
      setToast({ msg: next === 'bit_gated' ? '💎 DMs now Bit-gated' : '🔓 DMs are now open to everyone' });
    } catch {
      setDmGating(dmGating);  // revert
      setToast({ msg: 'Could not update DM setting' });
    }
  };

  // Blocked users list — Feb 2026. Users can block anyone from DM-ing
  // them (Members tile / DM header); this panel is the central unblock
  // console.
  const [blocks, setBlocks] = useState(null); // null=loading
  const loadBlocks = () => {
    api.get('/dm/blocks').then(({ data }) => setBlocks(data.blocks || [])).catch(() => setBlocks([]));
  };
  useEffect(() => { loadBlocks(); }, []);
  const unblock = async (handle) => {
    try {
      await api.delete(`/dm/block/${handle}`);
      setBlocks((prev) => (prev || []).filter((b) => b.handle !== handle));
      setToast({ msg: `Unblocked @${handle}` });
    } catch {
      setToast({ msg: `Could not unblock @${handle}` });
    }
  };

  const showComingSoon = (label) => setToast({ msg: `🚧 ${label} — coming soon.` });

  // Country + phone editing (Feb 2026) — mirrors Profile Setup. Users can
  // update or clear their entry any time. Save is manual (button click)
  // so we don't hammer PUT /profile on every keystroke.
  const [phoneCountry, setPhoneCountry] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneInitial, setPhoneInitial] = useState({ cc: '', num: '' });
  const [phoneBusy, setPhoneBusy] = useState(false);
  useEffect(() => {
    if (user) {
      const cc = user.phone_country_code || '';
      const num = user.phone_number || '';
      setPhoneCountry(cc);
      setPhoneNumber(num);
      setPhoneInitial({ cc, num });
    }
  }, [user]);
  const phoneDirty = phoneCountry !== phoneInitial.cc || phoneNumber.trim() !== phoneInitial.num;
  const savePhone = async () => {
    if (!phoneDirty || phoneBusy) return;
    setPhoneBusy(true);
    try {
      // Re-fetch the freshest profile snapshot before overwriting so we
      // never silently blank out display_name / bio / categories with a
      // stale local copy of `user`. Then patch just the two phone fields
      // onto that snapshot. (PUT /profile is a full-object replace right
      // now — a follow-up PATCH endpoint would let us skip this hop.)
      const { data: me } = await api.get('/auth/me');
      await api.put('/profile', {
        display_name: me.display_name || '',
        bio: me.bio || '',
        categories: me.categories || [],
        phone_country_code: phoneCountry,
        phone_number: phoneNumber.trim(),
      });
      await refreshMe?.();
      const cleared = !phoneCountry && !phoneNumber.trim();
      setToast({ msg: cleared ? 'Phone details cleared' : 'Phone details saved' });
      setPhoneInitial({ cc: phoneCountry, num: phoneNumber.trim() });
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not save phone details.' });
    } finally {
      setPhoneBusy(false);
    }
  };

  const canSavePw = curPw.length > 0 && newPw.length >= 8 && newPw === confirmPw && !savingPw;
  const savePassword = async () => {
    if (!canSavePw) return;
    setSavingPw(true);
    setPwErr('');
    setPwOk(false);
    try {
      await api.post('/auth/change-password', {
        current_password: curPw, new_password: newPw,
      });
      setCurPw(''); setNewPw(''); setConfirmPw('');
      setPwOk(true);
      setToast({ msg: '✓ Password updated' });
      setTimeout(() => { setPwOpen(false); setPwOk(false); }, 1200);
    } catch (ex) {
      setPwErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not change password.');
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <AppShell activeTab="wallet">
      <div className="settings-wrap" data-testid="settings-page">
        <h1 className="settings-title">Account settings</h1>
        <p className="settings-subtitle">
          Just your login credentials here. Head to your avatar for Profile, Referrals, Preferences, and your Clan.
        </p>

        {/* Invited-by chip — shown only if the current user signed up with someone's referral code */}
        {invitedBy && invitedBy.handle && (
          <a
            href={`/creator/${invitedBy.handle}`}
            className="invited-by-chip"
            data-testid="settings-invited-by"
            title={`Invited to Clanster by @${invitedBy.handle}`}
          >
            <span className="invited-by-label">Invited by</span>
            <span className="invited-by-handle">@{invitedBy.handle}</span>
            <span className="invited-by-arrow" aria-hidden="true">→</span>
          </a>
        )}

        <div className="group-label">Account</div>
        <div className="settings-card">
          <div className="settings-row" data-testid="settings-row-email">
            <div className="row-info">
              <div className="row-label">Email</div>
              <div className="row-value">{user?.email}</div>
            </div>
            <button
              className="row-action muted"
              onClick={() => { setEmOpen(true); setEmStep(1); setEmErr(''); }}
              data-testid="settings-change-email-btn"
            >
              Change
            </button>
          </div>
          <div className="settings-row" onClick={() => setPwOpen(true)} role="button" tabIndex={0} data-testid="settings-row-password">
            <div className="row-info">
              <div className="row-label">Password</div>
              <div className="row-value">••••••••</div>
            </div>
            <div className="row-chevron">›</div>
          </div>
          <div className="settings-row" data-testid="settings-row-2fa">
            <div className="row-info">
              <div className="row-label">Two-factor authentication</div>
              <div className="row-value">Extra security layer for your account.</div>
            </div>
            <span className="soon-pill" data-testid="settings-2fa-soon">Coming soon</span>
          </div>
        </div>

        {/* Change password panel */}
        {pwOpen && (
          <>
            <div className="pw-scrim" onClick={() => setPwOpen(false)} data-testid="pw-scrim" />
            <div className="pw-modal" data-testid="pw-modal">
              <div className="pw-modal-head">
                <h3>Change password</h3>
                <button className="pw-close" onClick={() => setPwOpen(false)} aria-label="Close" data-testid="pw-close-btn">×</button>
              </div>
              <div className="field">
                <div className="field-label"><span>Current password</span></div>
                <input
                  className="text-input"
                  type="password"
                  autoComplete="current-password"
                  value={curPw}
                  onChange={(e) => setCurPw(e.target.value)}
                  data-testid="pw-current-input"
                />
              </div>
              <div className="field">
                <div className="field-label"><span>New password</span></div>
                <input
                  className="text-input"
                  type="password"
                  autoComplete="new-password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  data-testid="pw-new-input"
                />
                <p className="pw-hint">At least 8 characters.</p>
              </div>
              <div className="field">
                <div className="field-label"><span>Confirm new password</span></div>
                <input
                  className="text-input"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  data-testid="pw-confirm-input"
                />
                {confirmPw.length > 0 && newPw !== confirmPw && (
                  <p className="pw-hint err">Passwords don't match.</p>
                )}
              </div>
              {pwErr && <p className="pw-hint err" data-testid="pw-err">{pwErr}</p>}
              {pwOk && <p className="pw-hint ok" data-testid="pw-ok">✓ Password updated</p>}
              <button
                className="btn-primary"
                onClick={savePassword}
                disabled={!canSavePw}
                data-testid="pw-save-btn"
              >
                {savingPw ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </>
        )}

        {/* Change email panel — 2-step OTP flow */}
        {emOpen && (
          <>
            <div className="pw-scrim" onClick={closeEmailModal} data-testid="em-scrim" />
            <div className="pw-modal" data-testid="em-modal">
              <div className="pw-modal-head">
                <h3>{emStep === 1 ? 'Change email' : 'Enter 6-digit code'}</h3>
                <button className="pw-close" onClick={closeEmailModal} aria-label="Close" data-testid="em-close-btn">×</button>
              </div>

              {emStep === 1 && (
                <>
                  <p className="pw-hint" style={{ marginTop: 0 }}>
                    We'll send a 6-digit code to the new email. Enter it back here to confirm.
                  </p>
                  <div className="field">
                    <div className="field-label"><span>New email</span></div>
                    <input
                      className="text-input"
                      type="email"
                      autoComplete="email"
                      value={emNewEmail}
                      onChange={(e) => setEmNewEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="em-new-email-input"
                    />
                  </div>
                  <div className="field">
                    <div className="field-label"><span>Current password</span></div>
                    <input
                      className="text-input"
                      type="password"
                      autoComplete="current-password"
                      value={emPassword}
                      onChange={(e) => setEmPassword(e.target.value)}
                      data-testid="em-password-input"
                    />
                  </div>
                  {emErr && <p className="pw-hint err" data-testid="em-err">{emErr}</p>}
                  <button
                    className="btn-primary"
                    onClick={requestOtp}
                    disabled={!emNewEmail || !emPassword || emBusy}
                    data-testid="em-send-code-btn"
                  >
                    {emBusy ? 'Sending code…' : 'Send code'}
                  </button>
                </>
              )}

              {emStep === 2 && (
                <>
                  <p className="pw-hint" style={{ marginTop: 0 }}>
                    Sent a 6-digit code to <b>{emNewEmail}</b>. It expires in 10 minutes.
                  </p>
                  <div className="field">
                    <div className="field-label"><span>6-digit code</span></div>
                    <input
                      className="text-input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      autoComplete="one-time-code"
                      value={emOtp}
                      onChange={(e) => setEmOtp(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      style={{ letterSpacing: '0.35em', fontSize: 20, fontWeight: 700, textAlign: 'center', fontFamily: 'JetBrains Mono, monospace' }}
                      data-testid="em-otp-input"
                    />
                  </div>
                  {emErr && <p className="pw-hint err" data-testid="em-err">{emErr}</p>}
                  <button
                    className="btn-primary"
                    onClick={confirmOtp}
                    disabled={emOtp.length !== 6 || emBusy}
                    data-testid="em-confirm-btn"
                  >
                    {emBusy ? 'Confirming…' : 'Confirm change'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEmStep(1); setEmOtp(''); setEmErr(''); }}
                    style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginTop: 12, padding: 4 }}
                    data-testid="em-back-btn"
                  >
                    ← Use a different email
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {/* Country + phone — Feb 2026. Same optional fields as Profile
            Setup so existing users can add or edit later. Save button
            only enables when there's a diff (avoids no-op PUT calls). */}
        <div className="group-label">Country & phone</div>
        <div className="settings-card" data-testid="phone-card">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 12px' }}>
            <div className="phone-row">
              <select
                className="text-input phone-country-select"
                value={phoneCountry}
                onChange={(e) => setPhoneCountry(e.target.value)}
                data-testid="settings-phone-country-select"
                aria-label="Country"
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {countryFlag(c.code)} {c.name} ({c.dial})
                  </option>
                ))}
              </select>
              <input
                className="text-input phone-number-input"
                type="tel"
                inputMode="tel"
                placeholder={
                  phoneCountry && COUNTRY_BY_CODE[phoneCountry]
                    ? `${COUNTRY_BY_CODE[phoneCountry].dial} 55 123 4567`
                    : 'Phone number'
                }
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.slice(0, 32))}
                data-testid="settings-phone-number-input"
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {phoneDirty && phoneInitial.cc === '' && phoneInitial.num === '' ? null : (
                <button
                  type="button"
                  className="mc-btn-secondary"
                  onClick={() => { setPhoneCountry(''); setPhoneNumber(''); }}
                  disabled={phoneBusy || (!phoneCountry && !phoneNumber)}
                  data-testid="settings-phone-clear-btn"
                  style={{ fontSize: 12, padding: '6px 12px' }}
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                className="btn-primary"
                onClick={savePhone}
                disabled={!phoneDirty || phoneBusy}
                data-testid="settings-phone-save-btn"
                style={{ padding: '8px 18px', fontSize: 13 }}
              >
                {phoneBusy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* Phase 9.7 — DM privacy toggle */}
        <div className="group-label">Direct messages</div>
        <div className="settings-card">
          <button
            type="button"
            className="settings-row"
            onClick={flipGating}
            disabled={dmGating === null}
            data-testid="dm-gating-row"
          >
            <div className="row-info">
              <div className="row-label">Who can DM me</div>
              <div className="row-value">
                {dmGating === 'bit_gated' ? '💎 Bit holders only' : '🔓 Anyone with a profile'}
              </div>
            </div>
            <div className="row-chevron">{dmGating === 'bit_gated' ? 'ON' : 'OFF'}</div>
          </button>
        </div>

        {/* Blocked users — Feb 2026. Central unblock console for people
            you've blocked from the Members tile or DM header. Blocks are
            one-directional (they can't DM you; you can still DM them). */}
        <div className="group-label">Blocked users</div>
        <div className="settings-card blocked-users-card" data-testid="blocked-users-card">
          {blocks === null && (
            <div className="blocked-users-empty">Loading…</div>
          )}
          {blocks !== null && blocks.length === 0 && (
            <div className="blocked-users-empty" data-testid="blocked-users-empty">
              You haven't blocked anyone. Tap 🚫 Block on any Members tile or DM header to add someone.
            </div>
          )}
          {blocks !== null && blocks.length > 0 && (
            <ul className="blocked-users-list">
              {blocks.map((b) => (
                <li key={b.handle} className="blocked-user-row" data-testid={`blocked-user-${b.handle}`}>
                  <div
                    className="blocked-user-avatar"
                    style={{ background: `linear-gradient(135deg, ${b.gradient[0]}, ${b.gradient[1]})` }}
                    aria-hidden="true"
                  >
                    {b.initials}
                  </div>
                  <div className="blocked-user-meta">
                    <div className="blocked-user-name">{b.display_name}</div>
                    <div className="blocked-user-handle">@{b.handle}</div>
                  </div>
                  <button
                    type="button"
                    className="blocked-user-unblock"
                    onClick={() => unblock(b.handle)}
                    data-testid={`unblock-btn-${b.handle}`}
                  >
                    Unblock
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Danger zone removed — sign-out lives in the avatar menu.
            Ref: /app/frontend/src/components/AppShell.jsx */}

        <p className="settings-footer">Clanster · Beta v0.5 · Made with 💜</p>
      </div>

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AppShell>
  );
}
